import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SearchAgentResult } from '../system/search';
import {
  Permission,
  PermissionCheck,
  PermissionMapping,
  toPermissionKey,
} from '../system/permission';
import { AccountMetadataFields } from '../system/account';
import { useBrand } from './BrandingContext';
import { useTenant } from './TenantContext';
import SaasLogout from '../components/common/saas/SaasLogout';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  session: Session | null;
  allowed: (check: PermissionCheck) => boolean;
  hasPermission: (permission: string | number) => boolean;
  checkAuthentication: () => Promise<{
    session?: Session;
    willNotLogin: boolean;
  }>;
  logout: () => Promise<void>;
}

interface ResoHeaders {
  reso_client_header_api_key: string;
  reso_client_header_api_secret: string;
}

export interface Session {
  user: {
    nameID: string;
    email: string;
    firstName: string;
    lastName: string;
    memberMlsId: string;
    agent: SearchAgentResult;
    tenant: string;
  };
  id: string;
  reso: ResoHeaders;
  permissions: Permission[];
  lastAccessed: number;
  createdAt: number;
  metadata?: AccountMetadataFields;
}
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);
interface ConfigProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: ConfigProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const { clearBranding } = useBrand();
  const { api, tenant, getConfigValue, isAddEditHost, isTenantFoundInPath } =
    useTenant();

  // Determine if tenant is using permissions from addedit
  const isUsingPermissions = getConfigValue<boolean>('ENABLE_PERMISSIONS');

  // Compute the frontend URL dynamically
  const frontendUrl = useMemo(() => {
    const { protocol, hostname, port } = window.location;
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }, []);

  // Check session status periodically
  useEffect(() => {
    const checkSessionStatus = async () => {
      if (isLoggedOut) {
        // Nothing to check here, application is stalled
        return;
      }

      try {
        const response = await fetch(`${api}/me`, {
          credentials: 'include', // Important for sending cookies
        });
        if (!response.ok) {
          await logout();
        }
      } catch (error) {
        await logout();
      }
    };

    const interval = setInterval(checkSessionStatus, 60000 * 5); // Check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const logout = async () => {
    try {
      await fetch(`${api}/auth/logout`, {
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error during logout API call:', error);
    } finally {
      // Clear branding to ensure it doesn't persist after logout
      clearBranding();

      // Clear local state regardless of logout API success
      setSession(null);
      setIsAuthenticated(false);

      // Fetch the tenant config for logout
      // This is usually a URL, but some tenants will not require a redirect
      // > In those cases, they will generally provide 'LOGOUT_LOCAL..'
      // Handle these cases by only observing HTTP logout configuration
      const logout = getConfigValue<string>('LOGOUT_URL');
      // Handle logout differently based on tenant
      if (!isAddEditHost() && logout && logout.startsWith('http')) {
        window.location.href = logout;
      } else {
        // For other tenants, show the logged out page
        setIsLoggedOut(true);
      }
    }
  };

  const hasPermission = (permission: string | number): boolean => {
    if (!isUsingPermissions) {
      return true;
    }

    const permissionValue = PermissionMapping[Number(permission)];
    return (
      !!session?.permissions.find((agentPermission: Permission) => {
        return agentPermission.PartyPermPermissionType === `${permissionValue}`;
      }) || false
    );
  };

  const allowed = ({ permission, level }: PermissionCheck): boolean => {
    if (!isUsingPermissions) {
      return true;
    }

    const permissionValue = toPermissionKey(permission);

    const permissions = session?.permissions.filter(
      (agentPermission: Permission) => {
        return agentPermission.PartyPermPermissionType === `${permissionValue}`;
      },
    );

    if (!permissions) {
      return false;
    }

    if (!level) {
      return permissions.length > 0;
    }

    return !!permissions.find(
      (permission) => permission.PartyPermAccessLevelType === level,
    );
  };

  const checkAuthentication = async (): Promise<{
    session?: Session;
    willNotLogin: boolean;
  }> => {
    const login = () => {
      if (!window.location.pathname.includes('auth-success')) {
        const currentUrl = window.location.href;

        // On the SAAS domain we can't assume the tenant is BRIGHT
        //   Tenant is provided in the domain
        //   addedit.mynextgensuite.com/mlsnow -> mlsnow'
        // So in this case, addedit.mynextgensuite.com/ can't be assumed
        // Reject the user with a 404
        if (isAddEditHost() && !isTenantFoundInPath) {
          return {
            willNotLogin: true,
            session: undefined,
          };
        }

        window.location.href = `${api}/auth/login?frontendUrl=${encodeURIComponent(frontendUrl)}&next=${encodeURIComponent(currentUrl)}&tenant=${tenant}`;
      }
      return {
        willNotLogin: false,
        session: undefined,
      };
    };
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${api}/me`, {
        credentials: 'include',
      });

      if (!response.ok) {
        // Handle unauthenticated state
        return login();
      } else {
        const sessionData = await response.json();
        setSession(sessionData);
        setIsAuthenticated(true);

        return {
          session: sessionData,
          willNotLogin: false,
        };
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error
          : new Error('Authentication check failed'),
      );
      return login();
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoggedOut) {
    return <SaasLogout />;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        logout,
        hasPermission,
        error,
        allowed,
        session,
        checkAuthentication,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
